// App-shell sidebar AI credits gauge (Surface 1 from #08).
// See plans/saas-billing/08-ai-credit-usage-display.md.
//
// Mounts in the global app shell so warning/depleted AI credit balances are
// visible from authenticated routes. Hides on the /billing/* routes (the hub
// page already owns the gauge), /chat (a launcher that moves to /home at once and
// opens the chat panel over the page; the panel itself sits above the gauge),
// when there is no subscription (new_signup_incomplete), or when the
// user is unauthenticated. On 'restricted' enforcement (#06) the gauge collapses to a
// lock icon that routes to /billing (per spec — the gauge becomes "pay your
// bill first" rather than "buy more credits").
//
// On mobile (<768px), the floating widget shrinks to an icon-only badge in the
// bottom-right, so it doesn't crowd the bottom tab bar of Ionic apps. On
// desktop the user can collapse the widget to a compact pill so it no longer
// blocks underlying content.


import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { IonIcon } from '@ionic/angular';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, startWith, switchMap, takeUntil } from 'rxjs/operators';

import {
  AICreditBalance,
  EnforcementStatus
} from '../../models/billing';
import { AuthService } from '../../services/auth.service';
import {
  IBillingMockService
} from '../../services/billing-mock/billing-mock.interface';
import { BillingMockService } from '../../services/billing-mock/billing-mock.service';
import { BILLING_SERVICE } from '../../services/billing-mock/billing.token';
import { LoggerService } from '../../services/logger.service';
import { isMobileViewport } from '../../utils/breakpoints';
import {
  BillingCreditsGaugeComponent,
  getCreditSeverity
} from './billing-credits-gauge.component';

export const BILLING_SIDEBAR_COLLAPSED_STORAGE_KEY = 'solo_ai_credit_sidebar_collapsed';

@Component({
    selector: 'app-billing-sidebar-credits-gauge',
    imports: [IonIcon, BillingCreditsGaugeComponent],
    template: `
    @if (shouldRender) {
      @if (!isRestricted) {
        @if (isDesktopCollapsed) {
          <button
            type="button"
            class="sidebar-gauge-collapsed"
            (click)="expandGauge()"
            aria-label="Show AI credits widget"
            title="Show AI credits widget"
            data-testid="sidebar-credits-gauge-expand">
            <span class="sidebar-gauge-collapsed__title">AI credits</span>
            <span class="sidebar-gauge-collapsed__meta">{{ collapsedSummary }}</span>
          </button>
        }
        @if (!isDesktopCollapsed) {
          <div
            class="sidebar-gauge"
            [class.sidebar-gauge--mobile]="isMobile"
            data-testid="sidebar-credits-gauge">
            @if (!isMobile) {
              <button
                type="button"
                class="sidebar-gauge-toggle"
                (click)="collapseGauge()"
                aria-label="Collapse AI credits widget"
                title="Collapse AI credits widget"
                data-testid="sidebar-credits-gauge-collapse">
                Hide
              </button>
            }
            @if (balance) {
              <app-billing-credits-gauge
                [balance]="balance"
                variant="compact">
              </app-billing-credits-gauge>
            }
          </div>
        }
      } @else {
        <button
          type="button"
          class="sidebar-gauge-lock"
          (click)="onLockClick()"
          aria-label="AI features paused while subscription needs attention. Open billing."
          title="AI features paused while subscription needs attention."
          data-testid="sidebar-credits-gauge-lock">
          <ion-icon name="lock-closed" aria-hidden="true"></ion-icon>
        </button>
      }
    }
    `,
    styles: [
        `
      .sidebar-gauge {
        position: fixed;
        right: 16px;
        /* S7: sit above the chat FAB (bottom: 24px + height 56px = 80px). */
        bottom: 96px;
        width: 280px;
        max-width: calc(100vw - 32px);
        z-index: 8500;
        pointer-events: auto;
      }
      .sidebar-gauge-toggle {
        position: absolute;
        top: 10px;
        right: 10px;
        z-index: 1;
        border: none;
        background: rgba(15, 23, 42, 0.08);
        color: var(--ion-text-color, #111827);
        border-radius: 999px;
        padding: 4px 10px;
        font-size: 12px;
        font-weight: 600;
        cursor: pointer;
      }
      .sidebar-gauge-toggle:hover {
        background: rgba(15, 23, 42, 0.14);
      }
      .sidebar-gauge ::ng-deep ion-card-header {
        padding-right: 68px;
      }
      .sidebar-gauge-collapsed {
        position: fixed;
        right: 16px;
        bottom: 96px;
        z-index: 8500;
        border: 1px solid rgba(148, 163, 184, 0.4);
        background: var(--ion-card-background, #ffffff);
        color: var(--ion-text-color, #111827);
        border-radius: 999px;
        padding: 10px 14px;
        box-shadow: 0 6px 18px rgba(15, 23, 42, 0.14);
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 124px;
        text-align: left;
        cursor: pointer;
      }
      .sidebar-gauge-collapsed__title {
        font-size: 12px;
        font-weight: 700;
      }
      .sidebar-gauge-collapsed__meta {
        font-size: 12px;
        color: var(--ion-color-medium, #6b7280);
      }
      .sidebar-gauge--mobile {
        width: 56px;
        height: 56px;
        right: 12px;
        bottom: 72px;
      }
      .sidebar-gauge--mobile ::ng-deep ion-card {
        width: 56px;
        height: 56px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;
      }
      .sidebar-gauge--mobile ::ng-deep ion-card-header,
      .sidebar-gauge--mobile ::ng-deep .credits-label,
      .sidebar-gauge--mobile ::ng-deep .credits-meta,
      .sidebar-gauge--mobile ::ng-deep .credits-reserved,
      .sidebar-gauge--mobile ::ng-deep .credits-topup,
      .sidebar-gauge--mobile ::ng-deep .credits-actions {
        display: none;
      }
      .sidebar-gauge--mobile ::ng-deep .credits-bar {
        width: 36px;
        margin: 0;
      }
      .sidebar-gauge-lock {
        position: fixed;
        right: 16px;
        bottom: 16px;
        width: 48px;
        height: 48px;
        border-radius: 50%;
        background: var(--ion-color-medium, #6b7280);
        color: #fff;
        border: none;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 22px;
        cursor: pointer;
        z-index: 8500;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
      }
      .sidebar-gauge-lock:hover {
        background: #4b5563;
      }
    `
    ]
})
export class BillingSidebarCreditsGaugeComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  balance: AICreditBalance | null = null;
  isRestricted = false;
  isHiddenRoute = false;
  isMobile = false;
  isAuthenticated = false;
  isCollapsed = false;
  isRouteSuppressed = false;

  constructor(
    @Inject(BILLING_SERVICE) private readonly billing: IBillingMockService,
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly logger: LoggerService
  ) {}

  ngOnInit(): void {
    this.isAuthenticated = !!this.authService.getAuthToken();
    this.isHiddenRoute = this.computeHidden(this.router.url);
    this.isRouteSuppressed = this.computeRouteSuppressed(this.router.url);
    this.isMobile = this.computeIsMobile();
    this.loadCollapsedPreference();

    this.authService.authSession$
      .pipe(takeUntil(this.destroy$))
      .subscribe(session => {
        this.isAuthenticated = !!session.token;
      });

    // Re-evaluate hide-on-route as the user navigates. Route changes only
    // affect visibility; they do not refetch billing data.
    this.router.events
      .pipe(
        filter((evt): evt is NavigationEnd => evt instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(evt => {
        const url = evt.urlAfterRedirects ?? evt.url;
        this.isHiddenRoute = this.computeHidden(url);
        this.isRouteSuppressed = this.computeRouteSuppressed(url);
      });

    // Re-evaluate mobile breakpoint on resize.
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.handleResize);
    }

    const mock = this.billing as Partial<BillingMockService>;
    const scenarioTrigger$: Observable<unknown> = mock.activeScenarioChanges$ ?? of(null);
    const authenticated$ = this.authService.authSession$.pipe(
      map(session => !!session.token),
      distinctUntilChanged()
    );
    const balance$: Observable<AICreditBalance | null> = combineLatest([
      scenarioTrigger$.pipe(startWith(null as unknown)),
      authenticated$
    ]).pipe(
      switchMap(([, isAuthenticated]) =>
        isAuthenticated ? this.billing.getAICreditBalance() : of(null)
      ),
      catchError(() => of(null))
    );
    const enforcement$: Observable<EnforcementStatus> = authenticated$.pipe(
      switchMap(isAuthenticated =>
        isAuthenticated
          ? this.billing.getEnforcementStatus()
          : of({ level: 'none', restrictedFeatures: [], ctaUrl: '/billing' } as EnforcementStatus)
      ),
      catchError(() => of({ level: 'none', restrictedFeatures: [], ctaUrl: '/billing' } as EnforcementStatus))
    );
    combineLatest([balance$, enforcement$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([balance, enforcement]: [AICreditBalance | null, EnforcementStatus]) => {
        this.balance = balance;
        this.isRestricted = enforcement.level === 'restricted';
      });
  }

  ngOnDestroy(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.handleResize);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isDesktopCollapsed(): boolean {
    return !this.isMobile && this.isCollapsed;
  }

  get shouldRender(): boolean {
    if (!this.isAuthenticated) {
      return false;
    }
    if (this.isHiddenRoute) {
      return false;
    }
    if (this.isRouteSuppressed) {
      return false;
    }
    if (this.isRestricted) {
      return true;
    }
    // No subscription = no wallet to display. Only true for new_signup_incomplete.
    if (this.balance === null) {
      return false;
    }
    return getCreditSeverity(this.balance) !== 'green';
  }

  get collapsedSummary(): string {
    if (this.balance === null) {
      return 'Show details';
    }
    return `${this.balance.usedCredits.toLocaleString()} used`;
  }

  collapseGauge(): void {
    this.isCollapsed = true;
    this.persistCollapsedPreference(true);
  }

  expandGauge(): void {
    this.isCollapsed = false;
    this.persistCollapsedPreference(false);
  }

  onLockClick(): void {
    this.logger.info(
      'BillingSidebarCreditsGaugeComponent',
      'onLockClick',
      'restricted-state lock icon clicked - routing to /billing',
      {}
    );
    this.router.navigateByUrl('/billing');
  }

  private computeHidden(url: string): boolean {
    // Hide on every /billing/* route (the page owns its own gauge), the public
    // signup/login routes, and the dev-auth + auth routes (no wallet exists).
    // /chat is the chat launcher only (Channels C0b): match that route exactly, with or without a query
    // (?threadId=, ?sessionId=, ?bot=), not every path that happens to start with "/chat".
    const path = url.split(/[?#]/)[0];
    if (url.startsWith('/billing') || path === '/chat' || path === '/chat/') {
      return true;
    }
    if (
      url.startsWith('/signup') ||
      url.startsWith('/login') ||
      url.startsWith('/not-found') ||
      url.startsWith('/verify') ||
      url.startsWith('/device-verify') ||
      url.startsWith('/factor-setup') ||
      url.startsWith('/dev-auth') ||
      url.startsWith('/auth')
    ) {
      return true;
    }
    if (url === '/' || url === '') {
      return true;
    }
    return false;
  }

  private computeRouteSuppressed(url: string): boolean {
    const path = url.split(/[?#]/, 1)[0];
    return path === '/apps/catalog/pages/credit-package-create' ||
      path === '/apps/accounts/pages/account-packages';
  }

  private computeIsMobile(): boolean {
    return isMobileViewport();
  }

  private readonly handleResize = (): void => {
    const next = this.computeIsMobile();
    if (next !== this.isMobile) {
      this.isMobile = next;
    }
  };

  private loadCollapsedPreference(): void {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      this.isCollapsed = window.localStorage.getItem(BILLING_SIDEBAR_COLLAPSED_STORAGE_KEY) === '1';
    } catch (error) {
      this.logger.error(
        'BillingSidebarCreditsGaugeComponent',
        'loadCollapsedPreference',
        'Failed to read AI credits sidebar preference',
        { error }
      );
      this.isCollapsed = false;
    }
  }

  private persistCollapsedPreference(collapsed: boolean): void {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      if (collapsed) {
        window.localStorage.setItem(BILLING_SIDEBAR_COLLAPSED_STORAGE_KEY, '1');
        return;
      }
      window.localStorage.removeItem(BILLING_SIDEBAR_COLLAPSED_STORAGE_KEY);
    } catch (error) {
      this.logger.error(
        'BillingSidebarCreditsGaugeComponent',
        'persistCollapsedPreference',
        'Failed to persist AI credits sidebar preference',
        { error, collapsed }
      );
    }
  }
}
